import { apiClient } from '@lib/axios';

import type {
  ClientListItem,
  ClientProfile,
  ClientStatus,
  CreateClientRequest,
  RecordConsentRequest,
  UpdateClientProfileRequest,
  UpdateClientRequest,
} from '../types';

// Top-level pagination block returned by list endpoints (see API spec §3 Pagination).
interface ClientListPagination {
  page: number;
  limit: number;
  total: number;
  hasNextPage: boolean;
}

interface ClientListEnvelope {
  success: boolean;
  data: ClientListItem[];
  pagination: ClientListPagination;
  meta: {
    requestId: string;
    timestamp: string;
  };
}

// Max page size accepted by GET /accounts/{accountId}/clients.
const MAX_PAGE_SIZE = 100;

export async function createClient(
  accountId: string,
  request: CreateClientRequest,
): Promise<ClientListItem> {
  const { data } = await apiClient.post<{ success: boolean; data: ClientListItem }>(
    `/clients/accounts/${accountId}/clients`,
    request,
  );
  return data.data;
}

export async function updateClient(
  accountId: string,
  clientId: string,
  request: UpdateClientRequest,
): Promise<ClientListItem> {
  const { data } = await apiClient.patch<{ success: boolean; data: ClientListItem }>(
    `/clients/accounts/${accountId}/clients/${clientId}`,
    request,
  );
  return data.data;
}

export async function updateClientProfile(
  accountId: string,
  clientId: string,
  request: UpdateClientProfileRequest,
): Promise<ClientProfile> {
  // PUT — full replacement of the updatable field set (API spec §5.4).
  const { data } = await apiClient.put<{ success: boolean; data: ClientProfile }>(
    `/clients/accounts/${accountId}/clients/${clientId}`,
    request,
  );
  return data.data;
}

export async function fetchClient(
  accountId: string,
  clientId: string,
): Promise<ClientProfile> {
  const { data } = await apiClient.get<{ success: boolean; data: ClientProfile }>(
    `/clients/accounts/${accountId}/clients/${clientId}`,
  );
  return data.data;
}

export async function recordConsent(
  accountId: string,
  clientId: string,
  request: RecordConsentRequest,
): Promise<void> {
  await apiClient.post(
    `/clients/accounts/${accountId}/clients/${clientId}/consents`,
    request,
  );
}

export async function fetchClientsByStatus(
  accountId: string,
  status: ClientStatus,
): Promise<ClientListItem[]> {
  const items: ClientListItem[] = [];
  let page = 1;
  let hasNextPage = true;

  while (hasNextPage) {
    const { data } = await apiClient.get<ClientListEnvelope>(
      `/clients/accounts/${accountId}/clients`,
      { params: { status, page, limit: MAX_PAGE_SIZE } },
    );

    items.push(...data.data);
    hasNextPage = data.pagination.hasNextPage;
    page += 1;
  }

  return items;
}
